import lang from "../../core/lang";
import i18n from "../../core/i18n";
import numeral from "../../core/numeral";
import time from "../../core/time";

/**
 * A Data Structure to facilitate the rendering of a table component.
 *
 * The selection is designed to preserve rows not in current dataset to support server-side paging.
 */
class TableData {
    data = undefined;
    columns = undefined;
    rowId = (datum, index) => index;
    selection = [];
    sorts = [];
    options = undefined;

    constructor(data = [], columns = [], options = {}) {
        const { rowId, selection, sorts } = options;
        this.data = data;
        this.columns = columns;
        this.options = options;
        rowId && (this.rowId = rowId);
        selection && (this.selection = selection);
        sorts && (this.sorts = [...sorts]);
    }

    // getHeadRow() {
    //     const headCells = this.columns.map((column, index) => {
    //         const { accessor } = column;
    //         const sort = this.sorts.find(sort => sort.property === accessor);
    //         const defaultRenderer = ({label}) => i18n.translate(label);
    //         const renderer = column.headRenderer || defaultRenderer;
    //         const content = renderer({...column});
    //         return new TableHeadCell(content, {...column, index, sort });
    //     });
    //     return new TableHeadRow(headCells);
    // }

    getDataRows(offset = 0, limit = 0) {
        const rowIdGenerator = lang.isFunction(this.rowId) ? this.rowId : (datum) => datum[this.rowId];

        const size = limit > 0 ? limit : this.data.length;
        const end = Math.min(offset + size, this.data.length);
        const data = this.data.slice(offset, end);

        const dataRows = data.map((datum, index) => {
            const row = offset + index;
            const rowId = rowIdGenerator(datum, row);

            const dataCells = this.columns.map((column, col) => {
                const { accessor } = column;
                const value = lang.isString(accessor) ? datum[accessor] : undefined;
                const defaultRenderer = lang.isFunction(accessor) ? accessor : (datum, {formatter, pattern}) => this.format(datum[accessor], formatter, pattern);
                const renderer = column.renderer || defaultRenderer;
                const content = renderer(datum, {...column, rowId, row, col});
                return new TableDataCell(value, content, col, {...column});
            });

            return new TableDataRow(dataCells, rowId, offset + row, datum);
        });
        return dataRows;
    }

    size() {
        return this.data.length;
    }

    select(rowId) {
        this.deselect(rowId);
        this.selection.push(rowId);
        return this.selection;
    }

    deselect(rowId) {
        this.selection = this.selection.filter(_rowId => _rowId !== rowId);
        return this.selection;
    }

    isSelected(rowId) {
        return this.selection.includes(rowId);
    }

    getSelection() {
        return this.selection;
    }

    /**
     * select all the rows in current dataset.
     *
     * @returns {*[]}
     */
    selectAll() {
        const _selection = this.getDataRows().map(row => row.rowId);
        this.selection = this.selection.filter(rowId => _selection.indexOf(rowId) === -1);
        this.selection = [...this.selection, ..._selection];
        return this.selection;
    }

    /**
     * deselect all the rows in current dataset.
     *
     * @returns {*[]}
     */
    deselectAll() {
        const _selection = this.getDataRows().map(row => row.rowId);
        this.selection = this.selection.filter(rowId => _selection.indexOf(rowId) === -1);
        return this.selection;
    }

    clearSelection() {
        const _selection = [];
        this.selection = _selection;
        return this.selection;
    }

    format(value, formatter, pattern) {
        let content;

        if(lang.isNullOrUndefined(formatter)) {
            if(lang.isNumber(value)) {
                formatter = numeral;
            } else if(lang.isDate(value)) {
                formatter = time;
            }
        }

        if(lang.isFunction(formatter)) {
            // use custom formatter first.
            content = formatter(value, pattern);
        } else if(lang.isArray(value)) {
            content = value.reduce((memo, item) => {
                const text = this.format(item, formatter, pattern);
                return `${memo}, ${text}`;
            }, '');
            content.length > 0 && (content = content.substring(2));
        } else if(lang.isObject(formatter)) {
            content = formatter.format(value, pattern);
        } else {
            content = value?.toString();
        }
        return content;
    }
}

class TableColumn {
    accessor = undefined;
    label = undefined;
    options = undefined;
    constructor(accessor, label, options = {}) {
        for(const key in options) {
            this.defineProperty(key, options[key]);
        }
        this.accessor = accessor;
        this.label = label;

        this.freezeProperties('accessor', 'label');
        for(const key in options) {
            this.freezeProperty(key);
        }
    }

    defineProperty(name, value) {
        Object.defineProperty(this, name, {
            value : value,
            enumerable : true,
            writable : true,
            configurable : true
        });
    }

    freezeProperty(name) {
        Object.defineProperty(this, name, {
            writable : false,
            configurable : true
        });
    }

    freezeProperties(names) {
        lang.isArray(names) || (names = Array.from(arguments));
        names.forEach(name => this.freezeProperty(name), this);
    }
}

class TableHeadRow {
    cells = undefined;
    constructor(cells = []) {
        this.cells = cells;
        this.freezeProperty('cells')
    }
    getCells() {
        return this.cells;
    }

    defineProperty(name, value) {
        Object.defineProperty(this, name, {
            value : value,
            enumerable : true,
            writable : true,
            configurable : true
        });
    }

    freezeProperty(name) {
        Object.defineProperty(this, name, {
            writable : false,
            configurable : true
        });
    }

    freezeProperties(names) {
        lang.isArray(names) || (names = Array.from(arguments));
        names.forEach(name => this.freezeProperty(name), this);
    }
}

class TableHeadCell {
    content = '';
    constructor(content, options = {}) {
        for(const key in options) {
            this.defineProperty(key, options[key]);
        }
        this.content = content;
        this.freezeProperty('content');
        for(const key in options) {
            this.freezeProperty(key);
        }
    }

    defineProperty(name, value) {
        Object.defineProperty(this, name, {
            value : value,
            enumerable : true,
            writable : true,
            configurable : true
        });
    }

    freezeProperty(name) {
        Object.defineProperty(this, name, {
            writable : false,
            configurable : true
        });
    }

    freezeProperties(names) {
        lang.isArray(names) || (names = Array.from(arguments));
        names.forEach(name => this.freezeProperty(name), this);
    }
}

class TableDataRow {
    cells = undefined;
    rowId = undefined;
    index = undefined;
    data = undefined;
    constructor(cells = [], rowId, index, data) {
        this.cells = cells;
        this.rowId = rowId;
        this.index = index;
        this.data = data;
        this.freezeProperty('cells');
        this.freezeProperty('rowId');
        this.freezeProperty('index');
        this.freezeProperty('data');
    }
    getCells() {
        return this.cells;
    }

    defineProperty(name, value) {
        Object.defineProperty(this, name, {
            value : value,
            enumerable : true,
            writable : true,
            configurable : true
        });
    }

    freezeProperty(name) {
        Object.defineProperty(this, name, {
            writable : false,
            configurable : true
        });
    }

    freezeProperties(names) {
        lang.isArray(names) || (names = Array.from(arguments));
        names.forEach(name => this.freezeProperty(name), this);
    }
}

class TableDataCell {
    content = '';
    constructor(value, content, index, options = {}) {
        for(const key in options) {
            this.defineProperty(key, options[key]);
        }
        this.content = content;
        this.value = value;
        this.index = index;
        this.freezeProperties('value', 'text', 'index');

        for(const key in options) {
            this.freezeProperty(key);
        }
    }

    defineProperty(name, value) {
        Object.defineProperty(this, name, {
            value : value,
            enumerable : true,
            writable : true,
            configurable : true
        });
    }

    freezeProperty(name) {
        Object.defineProperty(this, name, {
            writable : false,
            configurable : true
        });
    }
    freezeProperties(names) {
        lang.isArray(names) || (names = Array.from(arguments));
        names.forEach(name => this.freezeProperty(name), this);
    }
}

export { TableData as default, TableColumn };